"use client";

import { useEffect, useState } from "react";
import { createLogger } from "@/lib/logger";
import { authFetch } from "@/lib/authFetch";
import { FiLoader, FiPieChart } from "react-icons/fi";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

import AnimatedNumber from "@/app/components/AnimatedNumber";
import { useT, useTCategory } from "@/hooks/useT";

const logger = createLogger("CategoryBreakdown");

/** Animated bar that fills from 0% to target width */
function AnimatedBar({ pct, color }: { pct: number; color: string }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setWidth(pct));
    return () => cancelAnimationFrame(raf);
  }, [pct]);
  return (
    <div
      className="h-full rounded-[5px] transition-all duration-700 ease-out"
      style={{ width: `${width}%`, backgroundColor: color }}
    />
  );
}

interface Trade {
  id: string;
  marketSlug: string;
  marketQuestion: string;
  side: string;
  size: number;
  price: number;
  source?: string; // 'trade', 'copy', 'swap', 'polymarket'
  category?: string | null; // From user_activity table
}

interface CategoryData {
  category: string;
  tradeCount: number;
  totalVolumeUsd: number;
  [key: string]: string | number;
}

// Category colors - vibrant
const CATEGORY_COLORS: Record<string, string> = {
  crypto: "#F7931A",
  politics: "#6366F1",
  sports: "#22C55E",
  finance: "#3B82F6",
  culture: "#EC4899",
  tech: "#14B8A6",
  weather: "#60A5FA",
  economics: "#A855F7",
  science: "#84CC16",
  entertainment: "#F43F5E",
  other: "#6B7280",
};

// 2026-05-22 (Pika): the previous fallback for any unknown category
// dumped everything into the same gray (#6B7280), so when the
// backend returned categories outside the hardcoded map ("Economic
// policy", "Soccer", etc.) the breakdown rendered as 5 indistinct
// gray rows. Approach now: keep the explicit map for the canonical
// categories (crypto / politics / …) and for everything else,
// derive a deterministic hue from the category name itself, so
// "Soccer" always gets the same color across renders and across
// users. Same djb2 hash + HSL pattern we use for LeaderAvatar.
const HASH_SATURATION = 60;
const HASH_LIGHTNESS = 55;
function hashCategoryColor(category: string): string {
  let h = 5381;
  for (let i = 0; i < category.length; i++) h = ((h << 5) + h + category.charCodeAt(i)) | 0;
  const hue = Math.abs(h) % 360;
  return `hsl(${hue}, ${HASH_SATURATION}%, ${HASH_LIGHTNESS}%)`;
}

const getCategoryColor = (category: string): string => {
  const key = category.toLowerCase();
  if (CATEGORY_COLORS[key]) return CATEGORY_COLORS[key];
  // The literal "other" bucket keeps its neutral grey — that's the
  // long-tail residue, NOT an unknown category, and looking neutral
  // is the right semantic.
  if (key === "other") return CATEGORY_COLORS.other;
  return hashCategoryColor(key);
};

export default function CategoryBreakdown() {
  const t = useT();
  const tCategory = useTCategory();
  const [categories, setCategories] = useState<CategoryData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    const fetchTrades = async () => {
      try {
        setLoading(true);
        setError(null);

        // Use high limit to get all trades for accurate category breakdown
        const res = await authFetch("/api/portfolio/activity?limit=500");
        if (!res.ok) throw new Error("Failed to fetch activity");

        const data = await res.json();
        const allTrades: Trade[] = data.trades || [];

        // Filter out swaps and SELLs - only count BUYs for trading focus
        // A BUY represents entering a position (user's interest in that category)
        // A SELL is just exiting, not a new interest signal
        const trades = allTrades.filter(t =>
          t.source !== 'swap' &&
          t.side?.toUpperCase() === 'BUY'
        );

        if (trades.length === 0) {
          setCategories([]);
          return;
        }

        const categoryMap = new Map<string, { tradeCount: number; totalVolumeUsd: number }>();

        for (const trade of trades) {
          // Use category from user_activity table, fallback to "other"
          const category = (trade.category || "other").toLowerCase();
          const existing = categoryMap.get(category) || { tradeCount: 0, totalVolumeUsd: 0 };
          existing.tradeCount += 1;
          existing.totalVolumeUsd += (trade.size || 0) * (trade.price || 0);
          categoryMap.set(category, existing);
        }

        const categoriesArray: CategoryData[] = Array.from(categoryMap.entries())
          .map(([category, data]) => ({
            category,
            tradeCount: data.tradeCount,
            totalVolumeUsd: data.totalVolumeUsd,
          }))
          .sort((a, b) => b.tradeCount - a.tradeCount)
          .slice(0, 5);

        setCategories(categoriesArray);
      } catch (err) {
        logger.error("Error fetching activity", { error: err instanceof Error ? err.message : String(err) });
        setError(t("portfolio.categories.error"));
      } finally {
        setLoading(false);
      }
    };

    fetchTrades();
  }, []);

  const totalTrades = categories.reduce((sum, c) => sum + c.tradeCount, 0);

  const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: { payload: CategoryData }[] }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const percent = totalTrades > 0 ? ((data.tradeCount / totalTrades) * 100).toFixed(0) : 0;
      return (
        <div className="bg-gray-900 px-3 py-2 rounded-[5px] shadow-xl border border-gray-700 z-50 relative">
          <div className="flex items-center gap-2">
            <div
              className="w-2.5 h-2.5 rounded-[5px]"
              style={{ backgroundColor: getCategoryColor(data.category) }}
            />
            <span className="font-satoshi font-semibold text-sm text-white">
              {tCategory(data.category)}
            </span>
          </div>
          <div className="text-xs text-gray-400 mt-1">
            {t("portfolio.categories.tooltipTrades", { n: data.tradeCount, pct: percent })}
          </div>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-[#18181D] rounded-[5px] p-5 border border-gray-200 dark:border-[#1A1A1A] transition-colors h-full flex flex-col">
        {/* Skeleton matches loaded layout (header + donut chart + category bars) to prevent CLS */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-gray-200 dark:bg-[#1A1A1A] animate-pulse" />
            <div className="h-4 w-24 bg-gray-200 dark:bg-[#1A1A1A] rounded animate-pulse" />
          </div>
          <div className="h-5 w-16 bg-gray-200 dark:bg-[#1A1A1A] rounded-[5px] animate-pulse" />
        </div>
        <div className="flex-1 flex flex-col">
          <div className="w-full h-[140px] mb-4 flex items-center justify-center">
            <FiLoader className="w-6 h-6 text-gray-400 animate-spin" />
          </div>
          {/* Category bars placeholder — matches typical 5-row loaded state */}
          <div className="space-y-2.5">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-[5px] bg-gray-200 dark:bg-[#1A1A1A] animate-pulse" />
                    <div className="h-3 w-16 bg-gray-200 dark:bg-[#1A1A1A] rounded animate-pulse" />
                  </div>
                  <div className="h-3 w-10 bg-gray-200 dark:bg-[#1A1A1A] rounded animate-pulse" />
                </div>
                <div className="h-1.5 rounded-[5px] bg-gray-100 dark:bg-[#0f0f12]" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || categories.length === 0) {
    return (
      <div className="bg-white dark:bg-[#18181D] rounded-[5px] p-5 border border-gray-200 dark:border-[#1A1A1A] transition-colors h-full">
        <div className="flex items-center gap-2 mb-4">
          <FiPieChart className="w-4 h-4 text-gray-600 dark:text-gray-400" />
          <h4 className="font-satoshi font-semibold text-sm text-gray-900 dark:text-white">
            {t("portfolio.categories.title")}
          </h4>
        </div>
        <div className="h-[180px] flex flex-col items-center justify-center text-center">
          <FiPieChart className="w-10 h-10 text-gray-300 dark:text-[#1A1A1A] mb-3" />
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {error || t("portfolio.categories.empty")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-[#18181D] rounded-[5px] p-5 border border-gray-200 dark:border-[#1A1A1A] transition-colors h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FiPieChart className="w-4 h-4 text-gray-600 dark:text-gray-400" />
          <h4 className="font-satoshi font-semibold text-sm text-gray-900 dark:text-white">
            {t("portfolio.categories.title")}
          </h4>
        </div>
        <span className="px-2 py-0.5 rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] text-gray-700 dark:text-gray-300 text-xs font-medium tabular-nums">
          <AnimatedNumber value={totalTrades} /> {t("portfolio.categories.tradesSuffix")}
        </span>
      </div>

      {/* Chart — delay render until mounted to avoid Recharts -1 dimension warning */}
      <div className="flex-1 flex flex-col">
        <div className="w-full h-[140px] relative mb-4">
          {mounted ? <ResponsiveContainer width="100%" height="100%" minHeight={1} minWidth={1}>
            <PieChart>
              <Pie
                data={categories}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={65}
                dataKey="tradeCount"
                stroke="none"
                paddingAngle={2}
              >
                {categories.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={getCategoryColor(entry.category)}
                    className="hover:opacity-80 transition-opacity cursor-pointer"
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 100 }} />
            </PieChart>
          </ResponsiveContainer> : null}
          {/* Center - z-0 to stay below tooltip */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-0">
            <span className="font-monument text-2xl text-gray-900 dark:text-white tabular-nums"><AnimatedNumber value={totalTrades} /></span>
            <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t("portfolio.categories.tradesSuffix")}</span>
          </div>
        </div>

        {/* Category Bars */}
        <div className="space-y-2.5">
          {categories.map((cat) => {
            const percent = totalTrades > 0 ? (cat.tradeCount / totalTrades) * 100 : 0;
            return (
              <div key={cat.category} className="group">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-2 h-2 rounded-[5px]"
                      style={{ backgroundColor: getCategoryColor(cat.category) }}
                    />
                    <span className="text-xs text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                      {tCategory(cat.category)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 tabular-nums">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      <AnimatedNumber value={Math.round(percent)} />%
                    </span>
                    <span className="text-xs font-medium text-gray-900 dark:text-white w-6 text-right">
                      <AnimatedNumber value={cat.tradeCount} />
                    </span>
                  </div>
                </div>
                {/* Progress bar */}
                <div className="h-1.5 rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] overflow-hidden">
                  <AnimatedBar pct={percent} color={getCategoryColor(cat.category)} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
